import React, { FunctionComponent } from 'react';
import { Box, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import gaiaTheme from '../../../../root-components/theme';
import { axisClasses, LineChart } from '@mui/x-charts';

export const GraphLineChart: FunctionComponent<{
  title: string,
  tooltip?: string,
  dataset: Array<{}>,
  colors: string[],
  textColor?: string,
  series: Array<{ dataKey: string, label: string }>,
  dataKey: string,
  backgroundColor?: string,
  /** Rotates month/time labels and adds bottom margin so dense date ticks do not overlap */
  denseTimeAxis?: boolean,
  /** Hide the title/tooltip header row (e.g. when the caller renders its own fixed title). */
  hideHeader?: boolean,
  /** Draw the line in its final state at once (for the PDF render). */
  skipAnimation?: boolean,
  /** Chart height in px; defaults to the on-screen height. The PDF render uses a shorter chart. */
  chartHeight?: number,
}> = ({
  title,
  tooltip,
  dataset,
  series,
  dataKey,
  backgroundColor = 'white',
  colors,
  textColor = 'text.primary',
  denseTimeAxis = false,
  hideHeader = false,
  skipAnimation,
  chartHeight,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

    const xTickStyle = denseTimeAxis
      ? { fontSize: 11, angle: -40, textAnchor: 'end' as const }
      : { fontSize: 12 };

    return (
      <Paper
        variant='outlined'
        sx={{
          p: 4,
          pb: denseTimeAxis ? 5 : 4,
          backgroundColor: backgroundColor,
          border: backgroundColor === 'primary.dark' ? 'none' : '',
          overflow: 'hidden',
        }}
      >
        {!hideHeader && <Stack direction='row' alignItems={'center'}>
          <Typography variant='subtitle1' sx={{ flex: 1, color: textColor }}>
            <b>{title}</b>
          </Typography>
          {tooltip && !isMobileMode && <TooltipButton
            text={tooltip}
            noPadding={true}
            placement='right'
          />}
        </Stack>}
        <Box
          sx={{
            width: '100%',
            mt: 1,
            overflow: 'hidden',
            minHeight: chartHeight ?? (denseTimeAxis ? 420 : 400),
          }}
        >
          <LineChart
            dataset={dataset}
            xAxis={[{
              dataKey,
              scaleType: 'point',
              tickLabelStyle: xTickStyle,
            }]}
            yAxis={[{
            }]}
            series={series}
            grid={{ horizontal: true, vertical: false }}
            slotProps={{ legend: { hidden: true } }}
            margin={{ left: 60, right: 24, bottom: denseTimeAxis ? 96 : 36, top: 12 }}
            height={chartHeight ?? (denseTimeAxis ? 420 : 425)}
            colors={colors}
            skipAnimation={skipAnimation}
            sx={(theme) => ({
              [`.${axisClasses.root}`]: {
                [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                  stroke: textColor,
                  strokeWidth: 1,
                },
                [`.${axisClasses.tickLabel}`]: {
                  fill: textColor,
                },
              },
            })}
          />
        </Box>
      </Paper>
    )
  }